/**
 * P2-12: FastActions —— fastHunt / fastSacrifice / fastPraise 快捷按钮
 * （迁移自 WLeftPanel 内联段，js/jsx/left.jsx.js:856-925）。
 *
 * display/visibility 双层条件逐条对齐 INVENTORY：
 * - fastHunt：canHunt = (paragon.value > 0 || science("archery").researched)
 *   && !challenges.isActive("pacifism") → display:block/none；
 *   showFastHunt = manpower.value >= huntCost（huntCost = 100 - getEffect(
 *   "huntCatpowerDiscount")）→ visibility:visible/hidden（占位不消失，无冷却）；
 * - fastSacrifice：canSacrifice = religion.getHasUnlockedUnicornSacrifice() → display；
 *   getAvailableUnicornSacrifices() >= 1 → visibility；
 * - fastPraise：恒渲染；**可见性改为数据驱动 religion.faith > 0**——取代引擎
 *   updateFastPraise 的反向写 DOM（js/ui.js:699-717，INVENTORY 风险点 1）。
 *   旧左栏仍挂载期间由 js/ui.js 的桥接版 updateFastPraise（每 tick 重新 byId）
 *   驱动旧节点；本组件启用（P2-20）后桥接删除，二者不共存。
 *
 * 点击直调引擎：game.huntAll / game.sacrificeAllUnicorns / game.praise
 * （均消费 event.preventDefault()，game.js:4102-4115，故传入合成事件）。
 * id 与 class 钩子（fastHuntContainer 等、pin-link、pin-link-quantity、
 * sidebar-section）全部保留（主题 CSS 消费）。
 */
import { useCallback } from "react";
import type { MouseEvent } from "react";
import { getGame } from "../../bridge/GameBridge";
import type { GamePage } from "../../bridge/GameBridge";
import { useGameTick } from "../../bridge/useGameTick";
import { t } from "../../bridge/i18n";

// ---------------------------------------------------------------------------
// FastHunt
// ---------------------------------------------------------------------------

interface FastHuntSnapshot {
  canHunt: boolean;
  showFastHunt: boolean;
  countText: string;
  timesLabel: string;
}

function huntEq(a: FastHuntSnapshot | null, b: FastHuntSnapshot | null): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  return (
    a.canHunt === b.canHunt &&
    a.showFastHunt === b.showFastHunt &&
    a.countText === b.countText &&
    a.timesLabel === b.timesLabel
  );
}

export function computeFastHuntSnapshot(game: GamePage | null): FastHuntSnapshot | null {
  if (!game) {
    return null;
  }
  const huntCost = 100 - game.getEffect("huntCatpowerDiscount");
  const catpower = game.resPool.get("manpower");
  const huntCount = Math.floor(catpower.value / huntCost);

  const canHunt =
    (game.resPool.get("paragon").value > 0 || game.science.get("archery").researched) &&
    !game.challenges.isActive("pacifism");
  const showFastHunt = catpower.value >= huntCost;

  return {
    canHunt,
    showFastHunt,
    countText: game.getDisplayValueExt(huntCount, false, false, 0),
    timesLabel: huntCount === 1 ? t("left.hunt.time") : t("left.hunt.times"),
  };
}

export function FastHunt() {
  const selector = useCallback((game: GamePage | null) => computeFastHuntSnapshot(game), []);
  const snap = useGameTick(selector, huntEq);

  if (!snap) {
    return null;
  }
  return (
    <div
      id="fastHuntContainer"
      className="pin-link"
      style={{
        display: snap.canHunt ? "block" : "none",
        visibility: snap.showFastHunt ? "visible" : "hidden",
      }}
    >
      <a href="#" onClick={(e: MouseEvent) => getGame()?.huntAll(e)}>
        {t("left.hunt") + " ("}
        <span id="fastHuntContainerCount" className="pin-link-quantity">
          {snap.countText} {snap.timesLabel}
        </span>
        {")"}
      </a>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FastSacrifice
// ---------------------------------------------------------------------------

interface FastSacrificeSnapshot {
  canSacrifice: boolean;
  available: boolean;
  countText: string;
  timesLabel: string;
}

function sacrificeEq(a: FastSacrificeSnapshot | null, b: FastSacrificeSnapshot | null): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  return (
    a.canSacrifice === b.canSacrifice &&
    a.available === b.available &&
    a.countText === b.countText &&
    a.timesLabel === b.timesLabel
  );
}

export function computeFastSacrificeSnapshot(game: GamePage | null): FastSacrificeSnapshot | null {
  if (!game) {
    return null;
  }
  const canSacrifice = !!game.religion.getHasUnlockedUnicornSacrifice();
  const maxAvailableSacrifices = game.religion.getAvailableUnicornSacrifices();
  return {
    canSacrifice,
    available: maxAvailableSacrifices >= 1,
    countText: game.getDisplayValueExt(maxAvailableSacrifices, false, false, 0),
    timesLabel: maxAvailableSacrifices === 1 ? t("left.hunt.time") : t("left.hunt.times"),
  };
}

export function FastSacrifice() {
  const selector = useCallback((game: GamePage | null) => computeFastSacrificeSnapshot(game), []);
  const snap = useGameTick(selector, sacrificeEq);

  if (!snap) {
    return null;
  }
  return (
    <div
      id="fastSacrificeContainer"
      className="pin-link sidebar-section"
      style={{
        display: snap.canSacrifice ? "block" : "none",
        visibility: snap.available ? "visible" : "hidden",
      }}
    >
      <a href="#" onClick={(e: MouseEvent) => getGame()?.sacrificeAllUnicorns(e)}>
        {t("left.sacrifice") + " ("}
        <span id="fastSacrificeContainerCount" className="pin-link-quantity">
          {snap.countText} {snap.timesLabel}
        </span>
        {")"}
      </a>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FastPraise（数据驱动取代引擎反向写，见文件头注）
// ---------------------------------------------------------------------------

export function FastPraise() {
  // 对齐 updateFastPraise 的唯一语义：religion.faith > 0 → visible（js/ui.js:708-716）
  const visible = useGameTick((game) => (game ? game.religion.faith > 0 : null));

  if (visible === null) {
    return null;
  }
  return (
    <div
      id="fastPraiseContainer"
      className="pin-link sidebar-section"
      style={{ visibility: visible ? "visible" : "hidden" }}
    >
      <a href="#" onClick={(e: MouseEvent) => getGame()?.praise(e)}>
        {t("left.praise")}
      </a>
    </div>
  );
}

/**
 * 独立挂载入口，仅供隔离测试/调试。P2-20 起生产挂载由 LeftPanel 统一承担
 * （三个组件经命名导出复用），本入口不再注册到 KGNewUI.panels。
 * 纯展示组件，无需 TooltipProvider。
 */
export default function FastActionsPanel() {
  return (
    <>
      <FastHunt />
      <FastSacrifice />
      <FastPraise />
    </>
  );
}
